import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import {
  Plant, ArrowRight, ArrowLeft, Drop, Sun, Sparkle, MapPin, PlusCircle,
} from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { readLocation } from "@/components/LocationPill";
import { fpPath } from "@/lib/site";
import SEOHead from "@/components/SEOHead";
import ListenTranslateBar from "@/components/ListenTranslateBar";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/**
 * FarmersWhatToPlant — /farmers/plan-what-to-plant
 *
 * Signature "What should I plant?" tool.
 *
 * The farmer answers three quick questions — location, month, irrigated
 * or rain-fed — and receives a ranked list of crops in their local sow
 * window. Each card has a confidence, an emoji, a plant reason and a
 * one-tap "Add to My Farm" button that PATCHes the selected farm.
 */
export default function FarmersWhatToPlant() {
  const { user } = useAuth();
  const [loc, setLoc] = useState(() => {
    const s = readLocation();
    return s?.lat != null ? { lat: s.lat, lon: s.lon, label: s.city || s.country } : null;
  });
  const [month, setMonth] = useState(() => new Date().getMonth() + 1);
  const [waterMode, setWaterMode] = useState("either"); // irrigated | rain_fed | either
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [farms, setFarms] = useState([]);
  const [farmId, setFarmId] = useState("");
  const [adding, setAdding] = useState(null);

  useEffect(() => {
    if (!user) return;
    http.get("/farmers/farms/mine")
      .then(({ data }) => setFarms(data.farms || []))
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    const on = () => {
      const s = readLocation();
      if (s?.lat != null) setLoc({ lat: s.lat, lon: s.lon, label: s.city || s.country });
    };
    window.addEventListener("np:location-changed", on);
    return () => window.removeEventListener("np:location-changed", on);
  }, []);

  const canQuery = loc?.lat != null;

  useEffect(() => {
    if (!canQuery) return;
    setLoading(true);
    const params = new URLSearchParams({
      latitude: loc.lat, longitude: loc.lon, month: String(month),
    });
    http.get(`/farmers/calendar?${params}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load recommendations"))
      .finally(() => setLoading(false));
  }, [loc?.lat, loc?.lon, month, canQuery]);

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not available");
    navigator.geolocation.getCurrentPosition(
      (p) => setLoc({ lat: p.coords.latitude, lon: p.coords.longitude }),
      () => toast.error("Could not read your location"),
    );
  };

  // Rank sow-now crops with a simple confidence heuristic based on
  // climate-zone match + harvest length vs. season length.
  const sowNow = useMemo(() => {
    if (!data?.crops) return [];
    const zone = data.climate_zone;
    return data.crops
      .filter((c) => c.what_to_do_now === "sow")
      .map((c) => {
        const zoneMatch = (c.climate_zones || []).includes(zone) ? 1 : 0.5;
        const hd = c.harvest_days || 90;
        // Prefer crops that fit within a typical season
        const seasonFit = hd < 120 ? 1 : hd < 180 ? 0.8 : 0.6;
        // Rain-fed hint: shorter-cycle crops preferred when water is scarce
        const rainAdj = waterMode === "rain_fed" ? (hd < 100 ? 1.1 : 0.85)
                    : waterMode === "irrigated" ? 1
                    : 1;
        const conf = Math.min(0.98, 0.4 + 0.35 * zoneMatch + 0.2 * seasonFit) * rainAdj;
        const reason = zoneMatch === 1
          ? `Strong match for ${zone} climate`
          : `Common in nearby zones — verify locally`;
        const rating = conf >= 0.8 ? "Excellent" : conf >= 0.65 ? "Good" : "Moderate";
        return { ...c, _confidence: conf, _reason: reason, _rating: rating };
      })
      .sort((a, b) => b._confidence - a._confidence);
  }, [data, waterMode]);

  const addToFarm = async (crop) => {
    if (!user) return toast.error("Sign in to add to a farm");
    if (!farmId) return toast.error("Pick which farm to add this crop to");
    setAdding(crop.slug);
    try {
      const farm = farms.find((f) => f.id === farmId);
      const next = Array.from(new Set([...(farm?.crops || []), crop.common_name]));
      await http.patch(`/farmers/farms/${farmId}`, { crops: next });
      toast.success(`${crop.common_name} added to ${farm?.name || "your farm"}`);
      // Optimistically update local state so the button flips.
      setFarms((prev) => prev.map((f) => f.id === farmId ? { ...f, crops: next } : f));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add");
    } finally { setAdding(null); }
  };

  const narration = useMemo(() => {
    if (!sowNow.length) return "";
    const parts = [
      `What to plant right now — ${sowNow.length} crops recommended for ${data?.climate_zone || "your"} climate in ${MONTHS[month-1]}.`,
    ];
    for (const c of sowNow.slice(0, 6)) {
      parts.push(`${c.common_name}: ${c._rating}, ${Math.round(c._confidence * 100)}% confidence, ${c._reason}.`);
    }
    return parts.join(" ");
  }, [sowNow, data, month]);

  return (
    <main className="bg-[#F5EBDC] min-h-screen pt-32 md:pt-36 pb-32 px-5 md:px-10" data-testid="what-to-plant-page">
      <SEOHead
        title="What Should I Plant? — Location-Aware Planting Calendar | FarmersPulse™"
        description="Enter your location and month and FarmersPulse recommends crops in your local sow window with confidence + climate reasoning. One tap to add to your farm."
        path="/plan-what-to-plant"
      />
      <div className="max-w-[1100px] mx-auto">
        <Link to={fpPath("/")} className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830] hover:text-[#151312]">
          <ArrowLeft size={11} weight="bold" /> Farmers Pulse
        </Link>
        <div className="mt-3 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#2D8659] inline-flex items-center gap-1.5">
          <Plant size={12} weight="fill" /> Signature tool
        </div>
        <h1 className="mt-1 font-serif-display text-4xl md:text-5xl leading-[0.98] tracking-tight">
          What should I plant?
        </h1>
        <p className="mt-3 max-w-2xl text-sm md:text-base text-[#3A3830]">
          Pick your location, the month you plan to sow, and whether you can irrigate. FarmersPulse suggests crops in your local sow window ranked by confidence — one tap to add to your farm.
        </p>

        {narration && (
          <div className="mt-4">
            <ListenTranslateBar text={narration} testidPrefix="wtp-tools" />
          </div>
        )}

        {/* Controls */}
        <section className="mt-6 rounded-2xl border border-black/10 bg-white p-4 md:p-5 grid gap-4 md:grid-cols-3" data-testid="wtp-controls">
          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Location</div>
            <div className="mt-1 flex items-center gap-2">
              <div className="flex-1 text-sm text-[#151312]" data-testid="wtp-location">
                {loc ? `${loc.label || `${loc.lat.toFixed(2)}, ${loc.lon.toFixed(2)}`}` : "Not set"}
              </div>
              <button
                onClick={useMyLocation}
                data-testid="wtp-locate"
                className="rounded-full border border-black/15 bg-white px-3 py-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:border-[#2D8659] hover:text-[#2D8659]"
              >
                <MapPin size={10} weight="fill" className="inline mr-1" /> Use GPS
              </button>
            </div>
          </div>
          <div>
            <label className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Month</label>
            <select
              value={month}
              onChange={(e) => setMonth(parseInt(e.target.value, 10))}
              data-testid="wtp-month"
              className="mt-1 w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm"
            >
              {MONTHS.map((m, i) => (
                <option key={m} value={i + 1}>{m}</option>
              ))}
            </select>
          </div>
          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Water</div>
            <div className="mt-1 flex flex-wrap gap-1.5" data-testid="wtp-water-mode">
              {[
                { key: "irrigated", label: "💧 Irrigated", icon: Drop },
                { key: "rain_fed",  label: "🌧 Rain-fed",  icon: Sun },
                { key: "either",    label: "Either",       icon: Sparkle },
              ].map((m) => (
                <button
                  key={m.key}
                  onClick={() => setWaterMode(m.key)}
                  data-testid={`wtp-water-${m.key}`}
                  className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                    waterMode === m.key
                      ? "bg-[#0F3D2A] text-white border-[#0F3D2A]"
                      : "bg-white text-[#0F3D2A] border-black/15 hover:border-[#2D8659]"
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Add-to-farm target picker */}
        {user && farms.length > 0 && (
          <div className="mt-4 rounded-2xl border border-black/10 bg-white p-3 flex items-center gap-3 flex-wrap" data-testid="wtp-farm-picker">
            <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Add to farm</span>
            <select
              value={farmId}
              onChange={(e) => setFarmId(e.target.value)}
              data-testid="wtp-farm-select"
              className="bg-white border border-black/15 rounded-lg px-2 py-1 text-sm"
            >
              <option value="">— pick a farm —</option>
              {farms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </div>
        )}

        {/* Results */}
        <section className="mt-6" data-testid="wtp-results">
          {!canQuery ? (
            <div className="rounded-2xl border border-dashed border-black/20 p-8 text-center bg-white/70">
              <MapPin size={20} weight="fill" className="mx-auto text-[#2D8659] mb-2" />
              <div className="font-serif-display text-2xl">Set your location to get recommendations.</div>
              <button
                onClick={useMyLocation}
                data-testid="wtp-locate-hero"
                className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[#2D8659] text-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-[#0F3D2A]"
              >
                <MapPin size={11} weight="fill" /> Use my location
              </button>
            </div>
          ) : loading ? (
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#8E8B7F]">Loading recommendations…</div>
          ) : sowNow.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-black/20 p-8 text-center bg-white/70">
              <div className="font-serif-display text-xl">Nothing in the sow window for {MONTHS[month-1]}.</div>
              <p className="mt-1 text-sm text-[#3A3830]">Try another month or check the harvest calendar for crops currently maturing.</p>
            </div>
          ) : (
            <>
              <div className="mb-3 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#2D8659]" data-testid="wtp-summary">
                {sowNow.length} recommended · {data.climate_zone} climate · {MONTHS[month-1]} · {data.hemisphere} hemisphere
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {sowNow.map((c) => {
                  const farm = farms.find((f) => f.id === farmId);
                  const alreadyOn = farm && (farm.crops || []).includes(c.common_name);
                  return (
                    <div
                      key={c.slug}
                      data-testid={`wtp-crop-${c.slug}`}
                      className="rounded-2xl border border-black/10 bg-white p-4 hover:border-[#2D8659] transition-colors"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl" aria-hidden>{c.emoji || "🌱"}</span>
                          <div>
                            <div className="font-serif-display text-xl leading-tight text-[#151312]">{c.common_name}</div>
                            <div className="font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[#3A3830]">
                              {c.category || "crop"}
                            </div>
                          </div>
                        </div>
                        <div
                          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.24em] uppercase border ${
                            c._rating === "Excellent"
                              ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                              : c._rating === "Good"
                              ? "bg-amber-50 border-amber-200 text-amber-800"
                              : "bg-stone-50 border-stone-200 text-stone-700"
                          }`}
                          data-testid={`wtp-rating-${c.slug}`}
                        >
                          {c._rating} · {Math.round(c._confidence * 100)}%
                        </div>
                      </div>
                      <p className="mt-2 text-xs text-[#3A3830]">{c._reason}</p>
                      {c.harvest_days > 0 && (
                        <p className="mt-1 text-xs text-[#8E8B7F]">
                          ~{c.harvest_days} days to harvest
                        </p>
                      )}
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        <Link
                          to={fpPath(`/crops/${c.slug}`)}
                          className="rounded-full border border-black/15 bg-white px-3 py-1 font-mono-tech text-[9px] tracking-[0.24em] uppercase hover:border-[#2D8659] hover:text-[#2D8659]"
                          data-testid={`wtp-crop-details-${c.slug}`}
                        >
                          Details <ArrowRight size={9} weight="bold" className="inline ml-0.5" />
                        </Link>
                        {alreadyOn ? (
                          <span className="rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 px-3 py-1 font-mono-tech text-[9px] tracking-[0.24em] uppercase" data-testid={`wtp-on-farm-${c.slug}`}>
                            On this farm
                          </span>
                        ) : (
                          <button
                            onClick={() => addToFarm(c)}
                            disabled={!farmId || adding === c.slug}
                            data-testid={`wtp-add-to-farm-${c.slug}`}
                            title={!farmId ? "Pick a farm above to enable" : ""}
                            className="rounded-full bg-[#2D8659] text-white px-3 py-1 font-mono-tech text-[9px] tracking-[0.24em] uppercase hover:bg-[#0F3D2A] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1"
                          >
                            <PlusCircle size={9} weight="fill" /> {adding === c.slug ? "Adding…" : (!farmId ? "Pick a farm first" : "Add to My Farm")}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              {!user && (
                <p className="mt-4 text-xs text-[#3A3830]">
                  Sign in and add a farm to save these picks — one tap adds them to your crops list.
                </p>
              )}
            </>
          )}
        </section>
      </div>
    </main>
  );
}
